/**
 * Onglet Informations Professionnelles - ÉTAPE D
 * Mode lecture par défaut, édition en place
 * Salaire masqué par défaut avec audit trail
 */

import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";
import { 
  Edit2, Save, X, Eye, EyeOff, Loader2, Briefcase, Calendar, MapPin, Euro, AlertTriangle
} from "lucide-react";
import { DatePickerFr, formatDateFr } from "../../ui/date-picker-fr";
import {
  CONTRACT_TYPE_OPTIONS,
  EMPLOYEE_STATUS_OPTIONS,
  WORK_TIME_OPTIONS,
  TERMINATION_REASON_OPTIONS,
  getContractTypeLabel,
  getEmployeeStatusLabel,
  getWorkTimeTypeLabel,
  getTerminationReasonLabel,
  isFixedTermContract,
} from "../../../lib/hr-constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Section component
const Section = ({ title, icon: Icon, children, onEdit, isEditing, onSave, onCancel, isSaving, editDisabled }) => (
  <Card className="border-slate-200">
    <CardHeader className="pb-4">
      <div className="flex items-center justify-between">
        <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
          <Icon className="w-5 h-5 text-blue-600" />
          {title}
        </CardTitle>
        {!isEditing ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={onEdit}
                    disabled={editDisabled}
                    className={`text-slate-500 hover:text-blue-600 ${editDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid={`edit-${title.toLowerCase().replace(/\s+/g, '-')}-btn`}
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Modifier
                  </Button>
                </span>
              </TooltipTrigger>
              {editDisabled && (
                <TooltipContent>
                  <p>Enregistrez ou annulez les modifications en cours</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        ) : (
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onCancel}
              disabled={isSaving}
            >
              <X className="w-4 h-4 mr-1" />
              Annuler
            </Button>
            <Button 
              size="sm" 
              onClick={onSave}
              disabled={isSaving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Enregistrer
            </Button>
          </div>
        )}
      </div>
    </CardHeader>
    <CardContent className="pt-0">
      {children}
    </CardContent>
  </Card>
);

// Field display component with ALWAYS visible eye icon for sensitive fields
const Field = ({ label, value, hidden = false, onToggle, isEditing, onChange, type = "text", placeholder, options, warning, labelFormatter }) => {
  const [visible, setVisible] = useState(false);
  
  // Auto-hide after 30 seconds
  useEffect(() => {
    let timer;
    if (visible && hidden) {
      timer = setTimeout(() => {
        setVisible(false);
      }, 30000);
    }
    return () => clearTimeout(timer);
  }, [visible, hidden]);
  
  const handleToggle = () => {
    const newVisible = !visible;
    setVisible(newVisible);
    if (newVisible && onToggle) {
      onToggle();
    }
  };
  
  const maskedDisplay = "••••••••";
  const displayValue = hidden && !visible ? maskedDisplay : (value || "-");
  
  if (isEditing) {
    if (options) {
      return (
        <div className="space-y-1">
          <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</Label>
          <Select value={value || ""} onValueChange={onChange}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder={placeholder || "Sélectionner..."} />
            </SelectTrigger>
            <SelectContent>
              {options.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {warning && (
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {warning}
            </p>
          )}
        </div>
      );
    }
    
    // Use DatePicker for date fields
    if (type === "date") {
      return (
        <div className="space-y-1">
          <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</Label>
          <DatePickerFr
            value={value || ""}
            onChange={onChange}
            placeholder="JJ/MM/AAAA"
          />
          {warning && (
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {warning}
            </p>
          )}
        </div>
      );
    }
    
    return (
      <div className="space-y-1">
        <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</Label>
        <Input
          type={type}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="h-9"
        />
        {warning && (
          <p className="text-xs text-amber-600 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            {warning}
          </p>
        )}
      </div>
    );
  }
  
  // Display formatted date in French, or apply custom label formatter
  let displayFormatted;
  if (type === "date") {
    displayFormatted = formatDateFr(value);
  } else if (labelFormatter && value) {
    displayFormatted = labelFormatter(value);
  } else {
    displayFormatted = displayValue;
  }
  
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
      <div className="flex items-center gap-2">
        <p className={`text-sm text-slate-900 ${hidden ? 'font-mono' : ''}`}>
          {hidden && !visible ? maskedDisplay : displayFormatted}
        </p>
        {/* Eye icon ALWAYS visible for hidden fields */}
        {hidden && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6 text-slate-500 hover:text-blue-600" 
                  onClick={handleToggle}
                  data-testid={`toggle-${label.toLowerCase().replace(/\s+/g, '-')}-btn`}
                  disabled={!value}
                >
                  {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{visible ? "Masquer" : "Afficher"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {hidden && visible && (
          <span className="text-xs text-slate-400">(masqué dans 30s)</span>
        )}
      </div>
    </div>
  );
};

// Format currency
const formatCurrency = (value) => {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(num);
};

export default function ProfessionalInfoTab({ employee, onUpdate }) {
  const { token } = useAuth();
  const professional = employee.professional_info || {};
  
  // Edit states
  const [editingSection, setEditingSection] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showMatriculeWarning, setShowMatriculeWarning] = useState(false);
  const [pendingMatricule, setPendingMatricule] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({});
  
  // Start editing
  const startEditing = (section) => {
    setEditingSection(section);
    setFormData({
      matricule: employee.matricule || "",
      job_title: professional.job_title || "",
      department: professional.department || "",
      manager: professional.manager || "",
      hire_date: professional.hire_date || "",
      probation_end_date: professional.probation_end_date || "",
      termination_date: professional.termination_date || "",
      termination_reason: professional.termination_reason || "",
      contract_type: professional.contract_type || "",
      employee_status: professional.employee_status || "",
      collective_agreement: professional.collective_agreement || "",
      coefficient: professional.coefficient || "",
      work_time_type: professional.work_time?.type || "full_time",
      work_time_percentage: professional.work_time?.percentage?.toString() || "100",
      gross_salary: professional.gross_salary || "",
      work_location: professional.work_location || "",
    });
  };
  
  // Cancel editing
  const cancelEditing = () => {
    setEditingSection(null);
    setFormData({});
  };
  
  // Update field
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  // Save changes
  const saveChanges = async (section, confirmedMatricule = false) => {
    // Check if matricule is being changed
    if (section === "identification" && formData.matricule !== employee.matricule && !confirmedMatricule) {
      setPendingMatricule(formData.matricule);
      setShowMatriculeWarning(true);
      return;
    }
    
    setIsSaving(true);
    
    try {
      let payload = {};
      
      if (section === "identification") {
        payload = {
          matricule: formData.matricule,
          professional_info: {
            job_title: formData.job_title,
            department: formData.department || null,
            manager: formData.manager || null,
          }
        };
      } else if (section === "dates") {
        payload.professional_info = {
          hire_date: formData.hire_date,
          probation_end_date: formData.probation_end_date || null,
          termination_date: formData.termination_date || null,
          termination_reason: formData.termination_reason || null,
        };
      } else if (section === "contract") {
        payload.professional_info = {
          contract_type: formData.contract_type,
          employee_status: formData.employee_status,
          collective_agreement: formData.collective_agreement || null,
          coefficient: formData.coefficient || null,
        };
      } else if (section === "worktime") {
        payload.professional_info = {
          work_time: {
            type: formData.work_time_type,
            percentage: parseInt(formData.work_time_percentage) || 100,
          },
          work_location: formData.work_location || null,
        };
      } else if (section === "salary") {
        payload.professional_info = {
          gross_salary: formData.gross_salary || null,
        };
      }
      
      const response = await axios.put(`${API}/hr/employees/${employee.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      onUpdate(response.data);
      setEditingSection(null);
      setShowMatriculeWarning(false);
      setPendingMatricule(null);
      
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Erreur", { 
        description: error.response?.data?.detail || "Impossible d'enregistrer les modifications" 
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  // Log sensitive field access to audit trail
  const logFieldAccess = useCallback(async (fieldName) => {
    try {
      await axios.post(`${API}/hr/audit/reveal`, {
        employee_id: employee.id,
        field: fieldName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log(`[AUDIT] Field revealed: ${fieldName} for employee ${employee.id}`);
    } catch (error) {
      console.error("Error logging field access:", error);
    }
  }, [employee.id, token]);
  
  // Check if edit is disabled
  const isEditDisabled = (section) => editingSection !== null && editingSection !== section;

  return (
    <div className="space-y-6">
      {/* Identification */}
      <Section 
        title="Identification" 
        icon={Briefcase}
        isEditing={editingSection === "identification"}
        onEdit={() => startEditing("identification")}
        onCancel={cancelEditing}
        onSave={() => saveChanges("identification")}
        isSaving={isSaving}
        editDisabled={isEditDisabled("identification")}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field 
            label="Matricule" 
            value={editingSection === "identification" ? formData.matricule : employee.matricule}
            isEditing={editingSection === "identification"}
            onChange={(v) => updateField("matricule", v)}
            warning={editingSection === "identification" ? "Attention : modifier le matricule peut impacter les intégrations" : null}
          />
          <Field 
            label="Poste" 
            value={editingSection === "identification" ? formData.job_title : professional.job_title}
            isEditing={editingSection === "identification"}
            onChange={(v) => updateField("job_title", v)}
          />
          <Field 
            label="Département / Service" 
            value={editingSection === "identification" ? formData.department : professional.department}
            isEditing={editingSection === "identification"}
            onChange={(v) => updateField("department", v)}
          />
          <Field 
            label="Manager / Responsable" 
            value={editingSection === "identification" ? formData.manager : professional.manager}
            isEditing={editingSection === "identification"}
            onChange={(v) => updateField("manager", v)}
            placeholder="Nom du responsable"
          />
        </div>
      </Section>
      
      {/* Dates - Masquer champs non pertinents selon contrat/statut */}
      <Section 
        title="Dates clés" 
        icon={Calendar}
        isEditing={editingSection === "dates"}
        onEdit={() => startEditing("dates")}
        onCancel={cancelEditing}
        onSave={() => saveChanges("dates")}
        isSaving={isSaving}
        editDisabled={isEditDisabled("dates")}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field 
            label="Date d'embauche" 
            value={editingSection === "dates" ? formData.hire_date : professional.hire_date}
            isEditing={editingSection === "dates"}
            onChange={(v) => updateField("hire_date", v)}
            type="date"
          />
          <Field 
            label="Fin de période d'essai" 
            value={editingSection === "dates" ? formData.probation_end_date : professional.probation_end_date}
            isEditing={editingSection === "dates"}
            onChange={(v) => updateField("probation_end_date", v)}
            type="date"
          />
          {/* Date de fin: visible si contrat à durée déterminée OU si salarié sorti */}
          {(isFixedTermContract(professional.contract_type) || employee.status === "terminated") && (
            <Field 
              label="Date de fin de contrat" 
              value={editingSection === "dates" ? formData.termination_date : professional.termination_date}
              isEditing={editingSection === "dates"}
              onChange={(v) => updateField("termination_date", v)}
              type="date"
            />
          )}
          {/* Motif de sortie: visible uniquement si salarié sorti */}
          {employee.status === "terminated" && (
            <Field 
              label="Motif de sortie" 
              value={editingSection === "dates" ? formData.termination_reason : professional.termination_reason}
              isEditing={editingSection === "dates"}
              onChange={(v) => updateField("termination_reason", v)}
              options={TERMINATION_REASON_OPTIONS}
            />
          )}
        </div>
      </Section>
      
      {/* Contrat */}
      <Section 
        title="Contrat" 
        icon={Briefcase}
        isEditing={editingSection === "contract"}
        onEdit={() => startEditing("contract")}
        onCancel={cancelEditing}
        onSave={() => saveChanges("contract")}
        isSaving={isSaving}
        editDisabled={isEditDisabled("contract")}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field 
            label="Type de contrat" 
            value={editingSection === "contract" ? formData.contract_type : professional.contract_type}
            isEditing={editingSection === "contract"}
            onChange={(v) => updateField("contract_type", v)}
            options={CONTRACT_TYPE_OPTIONS}
            labelFormatter={getContractTypeLabel}
          />
          <Field 
            label="Statut" 
            value={editingSection === "contract" ? formData.employee_status : professional.employee_status}
            isEditing={editingSection === "contract"}
            onChange={(v) => updateField("employee_status", v)}
            options={EMPLOYEE_STATUS_OPTIONS}
            labelFormatter={getEmployeeStatusLabel}
          />
          <Field 
            label="Convention collective" 
            value={editingSection === "contract" ? formData.collective_agreement : professional.collective_agreement}
            isEditing={editingSection === "contract"}
            onChange={(v) => updateField("collective_agreement", v)}
            placeholder="SYNTEC, Métallurgie..."
          />
          <Field 
            label="Coefficient" 
            value={editingSection === "contract" ? formData.coefficient : professional.coefficient}
            isEditing={editingSection === "contract"}
            onChange={(v) => updateField("coefficient", v)}
          />
        </div>
      </Section>
      
      {/* Temps de travail */}
      <Section 
        title="Temps de travail" 
        icon={MapPin}
        isEditing={editingSection === "worktime"}
        onEdit={() => startEditing("worktime")}
        onCancel={cancelEditing}
        onSave={() => saveChanges("worktime")}
        isSaving={isSaving}
        editDisabled={isEditDisabled("worktime")}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field 
            label="Type" 
            value={editingSection === "worktime" ? formData.work_time_type : professional.work_time?.type}
            isEditing={editingSection === "worktime"}
            onChange={(v) => updateField("work_time_type", v)}
            options={WORK_TIME_OPTIONS}
            labelFormatter={getWorkTimeTypeLabel}
          />
          <Field 
            label="Pourcentage" 
            value={editingSection === "worktime" ? formData.work_time_percentage : professional.work_time?.percentage?.toString()}
            isEditing={editingSection === "worktime"}
            onChange={(v) => updateField("work_time_percentage", v)}
            type="number"
            placeholder="100"
          />
          <Field 
            label="Lieu de travail" 
            value={editingSection === "worktime" ? formData.work_location : professional.work_location}
            isEditing={editingSection === "worktime"}
            onChange={(v) => updateField("work_location", v)}
            placeholder="Paris, Lyon, Télétravail..."
          />
        </div>
      </Section>
      
      {/* Rémunération */}
      <Section 
        title="Rémunération" 
        icon={Euro}
        isEditing={editingSection === "salary"}
        onEdit={() => startEditing("salary")}
        onCancel={cancelEditing}
        onSave={() => saveChanges("salary")}
        isSaving={isSaving}
        editDisabled={isEditDisabled("salary")}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field 
            label="Salaire brut mensuel" 
            value={editingSection === "salary" 
              ? formData.gross_salary 
              : (professional.gross_salary ? formatCurrency(professional.gross_salary) : null)}
            isEditing={editingSection === "salary"}
            onChange={(v) => updateField("gross_salary", v)}
            type="number"
            placeholder="3500"
            hidden
            onToggle={() => logFieldAccess("gross_salary")}
          />
        </div>
      </Section>
      
      {/* Matricule change warning dialog */}
      <AlertDialog open={showMatriculeWarning} onOpenChange={setShowMatriculeWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Modifier le matricule ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Le matricule est un identifiant unique utilisé pour les intégrations 
              (paie, exports URSSAF, DSN...). Modifier ce champ peut créer des 
              incohérences dans d'autres systèmes.
              <br /><br />
              <strong>Ancien matricule :</strong> {employee.matricule}<br />
              <strong>Nouveau matricule :</strong> {pendingMatricule}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowMatriculeWarning(false)}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => saveChanges("identification", true)}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Confirmer la modification
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
